import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { API_BASE } from "@/lib/api";
import { CheckCircle, Warning, LockKey } from "@phosphor-icons/react";
import axios from "axios";
import { money } from "@/lib/money";

const fmtHol = (d) => new Date(d + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });

const TAX_CLASSIFICATIONS = [
  "Individual / sole proprietor",
  "C Corporation",
  "S Corporation",
  "Partnership",
  "Trust / estate",
  "Limited liability company",
  "Other",
];

// NOTE: These sub-components MUST live at module scope, not inside PublicPacket().
// If declared inside the parent, React re-creates them on every render, which
// unmounts every <input> subtree — that's the "loses focus after one keystroke"
// bug the user reported.
const Section = ({ title, subtitle, children }) => (
  <div className="bg-white border border-slate-200 rounded-md p-5 mb-4">
    <div className="tinylabel">{title}</div>
    {subtitle && <div className="text-xs text-slate-500 mt-0.5">{subtitle}</div>}
    <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">{children}</div>
  </div>
);
const Field = ({ label, children, full }) => (
  <div className={full ? "sm:col-span-2" : ""}>
    <label className="text-xs font-bold uppercase tracking-widest text-slate-500">{label}</label>
    <div className="mt-1">{children}</div>
  </div>
);
const Input = (props) => <input {...props} className={`w-full h-11 px-3 border border-slate-300 rounded-md ${props.className || ""}`} />;

export default function PublicPacket() {
  const { token } = useParams();
  const [packet, setPacket] = useState(null);
  const [err, setErr] = useState("");
  const [sent, setSent] = useState(false);
  const [assigned, setAssigned] = useState(false);
  const [busy, setBusy] = useState(false);

  const [f, setF] = useState({
    consent: false, signature_name: "",
    worker_name: "", worker_address: "", worker_phone: "", worker_email_confirmed: "",
    tax_type: "ssn", tax_id: "",
    emergency_contact_name: "", emergency_contact_phone: "",
    bank_name: "", account_type: "checking", ach_routing: "", ach_account: "",
    w9_legal_name: "", w9_business_name: "", w9_tax_classification: "Individual / sole proprietor", w9_address: "",
    w9_agree: false,
  });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    axios.get(`${API_BASE}/public/contractor-packets/${token}`)
      .then((r) => {
        setPacket(r.data);
        // Pre-fill the admin-known worker fields on every packet.
        const base = {
          worker_email_confirmed: r.data?.worker_email || "",
          worker_name: r.data?.worker_name || "",
          worker_phone: r.data?.worker_phone || "",
        };
        // For returning workers, also pre-fill from their existing worker
        // record (address, ach account type). Sensitive fields (SSN, ACH
        // routing/account) are never sent over the public token URL — the
        // worker either re-enters them to update OR leaves them blank to
        // keep the existing values on file (backend handles that fallback).
        if (r.data?.is_returning_worker && r.data?.prefill) {
          const pf = r.data.prefill;
          base.worker_name = pf.worker_name || base.worker_name;
          base.worker_phone = pf.worker_phone || base.worker_phone;
          base.worker_address = pf.worker_address || "";
          base.account_type = pf.account_type || "checking";
        }
        setF((p) => ({ ...p, ...base }));
      })
      .catch((e) => setErr(e.response?.data?.detail?.toString() || e.message));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!f.consent) return setErr("Please check the acceptance box before submitting.");
    if (!f.w9_agree) return setErr("Please check the W-9 attestation box before submitting.");
    if (f.signature_name.trim().toLowerCase() !== f.worker_name.trim().toLowerCase()) {
      return setErr("The typed signature must match the full name above.");
    }
    setBusy(true);
    try {
      const { w9_agree, consent, ...payload } = f;
      const { data } = await axios.post(`${API_BASE}/public/contractor-packets/${token}/submit`, payload);
      setAssigned(!!data?.assignment_created);
      setSent(true);
    } catch (er) {
      setErr(er.response?.data?.detail?.toString() || er.message);
    } finally { setBusy(false); }
  };

  if (err && !packet) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="max-w-md bg-white rounded-md border border-slate-200 p-8 text-center">
          <Warning size={40} weight="fill" className="text-rose-600 mx-auto" />
          <h1 className="text-xl font-black mt-3">Link unavailable</h1>
          <p className="text-sm text-slate-600 mt-2">{err}</p>
        </div>
      </div>
    );
  }
  if (!packet) return <div className="p-8">Loading…</div>;

  if (packet.already_completed || sent) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="max-w-md bg-white rounded-md border border-slate-200 p-8 text-center">
          <CheckCircle size={56} weight="fill" className="text-emerald-600 mx-auto" />
          <h1 className="text-2xl font-black mt-3">Packet submitted</h1>
          {assigned ? (
            <p className="text-sm text-slate-600 mt-2" data-testid="packet-submitted-assigned">Thank you — your assignment is set up. <b>Check your email (and phone)</b> for your assignment notice with your timesheet login details.</p>
          ) : (
            <p className="text-sm text-slate-600 mt-2">Thank you. Safety Onsite Solutions has received your onboarding packet and will be in touch about next steps.</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-3xl mx-auto p-4 md:p-8">
        <div className="mb-6">
          <div className="tinylabel text-emerald-700">Safety Onsite Solutions</div>
          <h1 className="text-3xl md:text-4xl font-black mt-1">Contractor Onboarding Packet</h1>
          <p className="text-slate-500 text-sm mt-2">Review the assignment terms below, then complete the form and sign electronically.</p>
        </div>

        <div className="bg-white border-2 border-slate-900 rounded-md p-5 mb-6">
          <div className="tinylabel">Assignment</div>
          <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
            <div><span className="text-slate-500">Client:</span> <b>{packet.company_name}</b></div>
            <div><span className="text-slate-500">Location:</span> {packet.location}</div>
            <div className="sm:col-span-2"><span className="text-slate-500">Scope:</span> {packet.description}</div>
            <div><span className="text-slate-500">Hourly rate:</span> <b className="font-mono">{money(packet.pay_rate)} / hr{packet.ot_rate_applies || packet.holiday_rate_applies ? "" : " (straight time only)"}</b></div>
            {packet.per_diem_applies && <div><span className="text-slate-500">Per diem:</span> <b className="font-mono">{money(packet.per_diem_rate)} / day</b></div>}
            {packet.ot_rate_applies && packet.ot_rate > 0 && (
              <div data-testid="public-packet-ot-terms"><span className="text-slate-500">Overtime:</span> <b className="font-mono">{money(packet.ot_rate)} / hr</b> <span className="text-slate-500">after {packet.ot_threshold_hours || 40} hrs/week</span></div>
            )}
            {packet.holiday_rate_applies && (
              <div className="sm:col-span-2" data-testid="public-packet-holiday-terms">
                <span className="text-slate-500">Holiday pay:</span> <b className="font-mono">{money(packet.holiday_pay_rate)} / hr</b>
                {(packet.holiday_dates || []).length > 0 && (
                  <span className="text-slate-600"> — designated holidays: <b>{packet.holiday_dates.map(fmtHol).join(", ")}</b></span>
                )}
                <span className="text-slate-500"> (in lieu of overtime, not in addition)</span>
              </div>
            )}
            <div><span className="text-slate-500">Start:</span> {packet.anticipated_start_date}</div>
            <div><span className="text-slate-500">Duration:</span> {packet.anticipated_duration}</div>
            <div className="sm:col-span-2"><span className="text-slate-500">Client contact:</span> {packet.contact_name} · {packet.contact_phone}</div>
          </div>
        </div>

        {packet.is_returning_worker && (
          <div data-testid="returning-worker-banner"
               className="bg-emerald-50 border-2 border-emerald-600 rounded-md p-4 mb-6 flex items-start gap-3">
            <CheckCircle size={28} weight="fill" className="text-emerald-600 shrink-0 mt-0.5" />
            <div className="text-sm text-emerald-900 leading-relaxed">
              <div className="font-bold text-base">Welcome back!</div>
              <div className="mt-1">
                We already have most of your information on file. You mainly need to
                <b> accept this new assignment</b> below. The personal, tax, and banking fields
                are pre-filled where we could. <b>Only update fields that have changed</b>.
                If you're not sure whether something changed, go ahead and complete the whole
                packet — that's totally fine too. Leaving a sensitive field
                (SSN, routing, account) blank keeps the one we have on file.
              </div>
            </div>
          </div>
        )}
        {packet.auto_assign && (
          <div data-testid="auto-assign-banner" className="bg-sky-50 border border-sky-200 rounded-md p-3 mb-6 text-sm text-sky-900">
            Once you sign and submit, your assignment is set up right away and you'll receive an email (and text, if we have your number) with your timesheet login details.
          </div>
        )}

        <form onSubmit={submit}>
          <div className="bg-white border border-slate-200 rounded-md p-5 mb-4">
            <div className="tinylabel text-rose-700">Legal acceptance</div>
            <div className="mt-3 p-4 bg-rose-50 border border-rose-200 rounded-md text-sm text-rose-900 leading-relaxed" data-testid="legal-text">
              {packet.legal_text}
            </div>
            <label className="mt-4 flex items-start gap-3 p-3 border border-slate-300 rounded-md cursor-pointer hover:bg-slate-50">
              <input type="checkbox" checked={f.consent} onChange={(e) => set("consent", e.target.checked)}
                data-testid="consent-checkbox" className="mt-1 h-4 w-4 accent-emerald-600 shrink-0" />
              <span className="text-sm text-slate-800">I have read and agree to the terms above.</span>
            </label>
          </div>

          <Section title="Personal information">
            <Field label="Full legal name" full><Input required value={f.worker_name} onChange={(e) => set("worker_name", e.target.value)} data-testid="worker_name" /></Field>
            <Field label="Home address" full><Input required value={f.worker_address} onChange={(e) => set("worker_address", e.target.value)} data-testid="worker_address" placeholder="Street, City, State, ZIP" /></Field>
            <Field label="Phone"><Input required type="tel" value={f.worker_phone} onChange={(e) => set("worker_phone", e.target.value)} data-testid="worker_phone" /></Field>
            <Field label="Email"><Input required type="email" value={f.worker_email_confirmed} onChange={(e) => set("worker_email_confirmed", e.target.value)} data-testid="worker_email_confirmed" /></Field>
            <Field label="Emergency contact name"><Input required value={f.emergency_contact_name} onChange={(e) => set("emergency_contact_name", e.target.value)} data-testid="emergency_contact_name" /></Field>
            <Field label="Emergency contact phone"><Input required type="tel" value={f.emergency_contact_phone} onChange={(e) => set("emergency_contact_phone", e.target.value)} data-testid="emergency_contact_phone" /></Field>
          </Section>

          <Section title="Tax identification" subtitle={packet.is_returning_worker
              ? "Leave blank to keep the one we have on file. Enter a new value only if it changed."
              : "Used for 1099 reporting. Encrypted at rest."}>
            <Field label="Payment made to" full>
              <div className="flex gap-3">
                <label className="flex-1 flex items-center gap-2 p-3 border border-slate-300 rounded-md cursor-pointer hover:bg-slate-50 has-[:checked]:bg-emerald-50 has-[:checked]:border-emerald-600">
                  <input type="radio" name="tax_type" value="ssn" checked={f.tax_type === "ssn"} onChange={() => set("tax_type", "ssn")} data-testid="tax_type_ssn" />
                  <span className="text-sm">Me personally (SSN)</span>
                </label>
                <label className="flex-1 flex items-center gap-2 p-3 border border-slate-300 rounded-md cursor-pointer hover:bg-slate-50 has-[:checked]:bg-emerald-50 has-[:checked]:border-emerald-600">
                  <input type="radio" name="tax_type" value="ein" checked={f.tax_type === "ein"} onChange={() => set("tax_type", "ein")} data-testid="tax_type_ein" />
                  <span className="text-sm">My business (EIN)</span>
                </label>
              </div>
            </Field>
            <Field label={f.tax_type === "ssn" ? "Social Security Number" : "EIN"} full>
              <Input required={!packet.is_returning_worker} value={f.tax_id} onChange={(e) => set("tax_id", e.target.value)}
                data-testid="tax_id" placeholder={packet.is_returning_worker
                  ? "On file — leave blank to keep, or enter new to update"
                  : (f.tax_type === "ssn" ? "XXX-XX-XXXX" : "XX-XXXXXXX")} />
            </Field>
          </Section>

          <Section title="Direct deposit banking" subtitle={packet.is_returning_worker
              ? "Leave routing/account blank to keep the one we have on file. Enter a new value only if it changed."
              : "For payment of contractor invoices. Account and routing numbers are encrypted at rest."}>
            <Field label="Bank / financial institution"><Input required={!packet.is_returning_worker} value={f.bank_name} onChange={(e) => set("bank_name", e.target.value)} data-testid="bank_name" placeholder={packet.is_returning_worker ? "Optional — leave blank if unchanged" : ""} /></Field>
            <Field label="Account type">
              <select value={f.account_type} onChange={(e) => set("account_type", e.target.value)}
                data-testid="account_type" className="w-full h-11 px-3 border border-slate-300 rounded-md bg-white">
                <option value="checking">Checking</option>
                <option value="savings">Savings</option>
              </select>
            </Field>
            <Field label="Routing number"><Input required={!packet.is_returning_worker} value={f.ach_routing} onChange={(e) => set("ach_routing", e.target.value)} data-testid="ach_routing" placeholder={packet.is_returning_worker ? "Leave blank to keep on file" : "9 digits"} /></Field>
            <Field label="Account number"><Input required={!packet.is_returning_worker} value={f.ach_account} onChange={(e) => set("ach_account", e.target.value)} data-testid="ach_account" placeholder={packet.is_returning_worker ? "Leave blank to keep on file" : ""} /></Field>
          </Section>

          <Section title="W-9 (Request for Taxpayer Identification Number)" subtitle="Required by the IRS for 1099-eligible contractors.">
            <Field label="Legal name (as shown on tax return)" full><Input required value={f.w9_legal_name} onChange={(e) => set("w9_legal_name", e.target.value)} data-testid="w9_legal_name" /></Field>
            <Field label="Business name / DBA (if different)" full><Input value={f.w9_business_name} onChange={(e) => set("w9_business_name", e.target.value)} data-testid="w9_business_name" /></Field>
            <Field label="Federal tax classification" full>
              <select required value={f.w9_tax_classification} onChange={(e) => set("w9_tax_classification", e.target.value)}
                data-testid="w9_tax_classification" className="w-full h-11 px-3 border border-slate-300 rounded-md bg-white">
                {TAX_CLASSIFICATIONS.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="Address on W-9" full><Input required value={f.w9_address} onChange={(e) => set("w9_address", e.target.value)} data-testid="w9_address" /></Field>
            <div className="sm:col-span-2 mt-2 p-4 bg-slate-50 border border-slate-200 rounded-md text-xs text-slate-700 leading-relaxed">
              <b>Certification.</b> Under penalties of perjury, I certify that: (1) the number shown on this form is my correct taxpayer identification number, (2) I am not subject to backup withholding because (a) I am exempt, (b) I have not been notified by the IRS that I am subject to backup withholding, or (c) the IRS has notified me that I am no longer subject to backup withholding, and (3) I am a U.S. citizen or other U.S. person as defined in the Form W-9 instructions.
            </div>
            <label className="sm:col-span-2 mt-1 flex items-start gap-3 p-3 border border-slate-300 rounded-md cursor-pointer hover:bg-slate-50">
              <input type="checkbox" checked={f.w9_agree} onChange={(e) => set("w9_agree", e.target.checked)}
                data-testid="w9_agree" className="mt-1 h-4 w-4 accent-emerald-600 shrink-0" />
              <span className="text-sm text-slate-800">I certify the above under penalty of perjury.</span>
            </label>
          </Section>

          <div className="bg-slate-900 text-white rounded-md p-5 mb-6">
            <div className="tinylabel text-emerald-400">Electronic signature</div>
            <p className="text-sm text-slate-300 mt-2">By typing your full legal name below exactly as entered above and clicking Submit, you are signing this Contractor Packet and W-9 electronically. Your submission is timestamped and IP-logged for audit.</p>
            <div className="mt-4">
              <label className="text-xs font-bold uppercase tracking-widest text-slate-400">Type your full name to sign</label>
              <input required value={f.signature_name} onChange={(e) => set("signature_name", e.target.value)}
                data-testid="signature_name"
                className="mt-1 w-full h-12 px-3 border border-slate-700 bg-slate-800 text-white rounded-md text-lg font-serif italic" />
            </div>
          </div>

          {err && <div className="mb-4 text-sm bg-rose-50 border border-rose-200 text-rose-800 rounded-md p-3">{err}</div>}

          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 justify-between">
            <div className="text-xs text-slate-500 flex items-center gap-1"><LockKey size={14} /> Your SSN and banking info are encrypted before storage.</div>
            <button type="submit" disabled={busy} data-testid="packet-submit-btn"
              className="w-full sm:w-auto h-12 px-6 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-md text-sm disabled:opacity-60">
              {busy ? "Submitting…" : "Sign & Submit Packet"}
            </button>
          </div>

          <div className="mt-6 text-xs text-slate-400 text-center">
            <Link to="/privacy" className="hover:text-slate-700">Privacy</Link> · <Link to="/terms" className="hover:text-slate-700">Terms</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
